import type { JSX, ReactNode } from 'react';
import React, { useEffect, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button, Checkbox } from '@feathr/components';
import type { IUseTableSelectionReturn } from '@feathr/extender/hooks';
import { useActionBar } from '@feathr/extender/state';

import * as styles from './SelectionActionBar.css';

interface ISelectionActionBarProps {
  /**
   * Selection object from useTableSelection hook
   */
  selection: IUseTableSelectionReturn;

  /**
   * Action buttons to display (e.g., Delete button)
   */
  actions?: ReactNode;

  /**
   * Pre-translated text for selected items (e.g., "3 people selected")
   * Takes precedence over itemLabel when provided
   */
  selectedText?: string;

  /**
   * Pre-translated text for select all mode (e.g., "All 150 people selected")
   * Used when isSelectAllMode is true
   */
  selectAllSelectedText?: string;

  /**
   * Pre-translated text for select all button (e.g., "Select all 150 people")
   * Used for the "Select all" button text
   */
  selectAllButtonText?: string;

  /**
   * @deprecated Use selectedText instead for full control over pluralization
   * Custom label text for selected items (fallback for backward compatibility)
   */
  itemLabel?: string;
}

function SelectionActionBar({
  selection,
  actions,
  selectedText,
  selectAllSelectedText,
  selectAllButtonText,
  itemLabel = 'person',
}: Readonly<ISelectionActionBarProps>): JSX.Element | null {
  const { t } = useTranslation();
  const { setLeftActions, setRightActions } = useActionBar();

  // Extract values from selection object
  const { count, hasSelection, isSelectAllMode, totalCount, clearSelection, selectAll } = selection;

  // Memoize the left content to prevent recreating it on every render
  const leftContent = useMemo(() => {
    if (!hasSelection) {
      return undefined;
    }

    // Generate selection label text
    const getSelectionLabel = (): ReactNode => {
      if (isSelectAllMode && totalCount && selectAllSelectedText) {
        // Use pre-translated "All X items selected" text
        return selectAllSelectedText;
      } else if (selectedText) {
        // Use pre-translated "X items selected" text
        return selectedText;
      } else {
        // Fallback to Trans component for backward compatibility
        return isSelectAllMode && totalCount ? (
          <Trans count={totalCount} t={t}>
            <strong>
              All {{ count: totalCount }} {{ itemLabel }}
            </strong>{' '}
            selected
          </Trans>
        ) : (
          <Trans count={count} t={t}>
            <strong>
              {{ count }} {{ itemLabel }}
            </strong>{' '}
            selected
          </Trans>
        );
      }
    };

    return (
      <div className={styles.leftContent}>
        <Checkbox
          className={styles.selectionCheckbox}
          label={getSelectionLabel()}
          onChange={clearSelection}
          value={true}
        />

        {!isSelectAllMode && selectAll && totalCount && totalCount > count && (
          <Button onClick={selectAll} type={'link'}>
            {selectAllButtonText ||
              t('Select all {{ count }} {{ itemLabel }}', { count: totalCount, itemLabel })}
          </Button>
        )}

        {isSelectAllMode && (
          <Button onClick={clearSelection} type={'link'}>
            {t('Clear selection')}
          </Button>
        )}
      </div>
    );
  }, [
    hasSelection,
    count,
    isSelectAllMode,
    totalCount,
    selectedText,
    selectAllSelectedText,
    selectAllButtonText,
    itemLabel,
    clearSelection,
    selectAll,
    t,
  ]);

  // Update action bar when selection state changes
  useEffect(() => {
    setLeftActions(leftContent);
    setRightActions(hasSelection ? actions : undefined);

    // Cleanup when component unmounts
    return () => {
      setLeftActions(undefined);
      setRightActions(undefined);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leftContent, hasSelection, actions]);

  // This component only manages the action bar, doesn't render anything itself
  return null;
}

export default SelectionActionBar;
